import { passesColumnFilters } from './columnLayout.js';

// Filtering for the Milestones page's Reporting sub-tab: one status (Awaiting
// unless told otherwise), an optional forecast-date window, and the filter row
// under the table's headers. A month picker is just a shortcut for a from/to
// range, so the tab resolves it to one.

export function monthRange(month) {
  if (!month) return { from: '', to: '' };
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

export function filterMilestones(rows, { status = 'Awaiting', from = '', to = '', columns = {} } = {}) {
  return rows.filter(m => {
    if (status !== 'All' && m.status !== status) return false;
    if (!passesColumnFilters(m, REPORT_FILTER_COLUMNS, columns)) return false;
    if (!from && !to) return true;
    if (!m.forecast_date) return false;
    if (from && m.forecast_date < from) return false;
    if (to && m.forecast_date > to) return false;
    return true;
  });
}

// The PO a milestone is billed against: its own customer PO where one was
// recorded, otherwise the SOW's, which is where most POs live.
export const milestonePo = (m) => m.customer_po || m.sow_po_number || '';

// The filter row, keyed by the table column each filter sits under. Status and
// forecast date have their own controls above the table, so they have none
// here. Text boxes match anywhere in the value, ignoring case; pick-lists
// offer the values in the rows the status and date filters leave.
const has = (v, q) => String(v || '').toLowerCase().includes(String(q).trim().toLowerCase());
const RAG_OPTIONS = [['red', 'Red'], ['amber', 'Amber'], ['green', 'Green'], ['none', 'No RAG']];

export const REPORT_FILTER_COLUMNS = [
  { key: 'avm', filter: { text: 'AVM, SOW or client…' },
    match: (m, q) => [m.milestone_code, m.reference, m.sow_detail, m.client_name].some(v => has(v, q)) },
  { key: 'po', filter: { text: 'Filter PO…' }, match: (m, q) => has(milestonePo(m), q) },
  { key: 'po_line_item', filter: { text: 'Filter line…' }, match: (m, q) => has(m.po_line_item, q) },
  { key: 'atv_pm', filter: { all: 'All ATV PMs', value: m => m.pm_name } },
  { key: 'client_pm', filter: { all: 'All client PMs', value: m => m.client_contact_name } },
  { key: 'rag', filter: { all: 'All RAGs', value: m => (m.sow_overall_rag || '').toLowerCase() || 'none', options: () => RAG_OPTIONS } },
  { key: 'dependencies', filter: { all: 'All dependencies', value: m => m.dependency_reason } },
  { key: 'summary', filter: { text: 'Filter summary…' }, match: (m, q) => has(m.summary, q) },
];

// The Reporting tab's CSV: the same rows and columns the table shows, with the
// dependency reason and comment joined as the table joins them. The table loads
// each row's history on demand, so the export fetches the contract and forecast
// date changes for every milestone in one go and passes them in, keyed by
// milestone id, to fill the last column.
const RAG_LABEL = { red: 'Red', amber: 'Amber', green: 'Green' };
const CHANGE_LABEL = { contract_date: 'Contract', forecast_date: 'Forecast' };

export const REPORT_CSV_HEADERS = ['AVM', 'SOW', 'SOW Name', 'Client', 'PO', 'PO Line Item', 'ATV PM', 'Client PM', 'Status', 'Contract Date', 'Forecast Date', 'RAG', 'Dependencies', 'Summary', 'Date Changes'];

// One line per change, oldest first: "2026-10-12 Jo Bloggs: Forecast 2026-10-01 to 2026-10-20".
// "to" rather than an arrow, because Excel opens a CSV without a BOM as ANSI.
export function dateChangesText(changes = []) {
  return [...changes]
    .sort((a, b) => String(a.changed_at || '').localeCompare(String(b.changed_at || '')))
    .map(c => `${String(c.changed_at || '').slice(0, 10)}${c.changed_by ? ` ${c.changed_by}` : ''}: ${CHANGE_LABEL[c.field_name] || c.field_name} ${c.old_value || 'none'} to ${c.new_value || 'none'}`)
    .join('\n');
}

// Group the /milestones/date-changes rows by milestone for reportCsvRows.
export function groupDateChanges(rows = []) {
  const by = {};
  for (const r of rows) (by[r.milestone_id] ||= []).push(r);
  return by;
}

export function reportCsvRows(rows, changesById = {}) {
  return rows.map(m => [
    m.milestone_code || '',
    m.reference || '',
    m.sow_detail || '',
    m.client_name || '',
    milestonePo(m),
    m.po_line_item || '',
    m.pm_name || '',
    m.client_contact_name || '',
    m.status || '',
    m.contract_date || '',
    m.forecast_date || '',
    RAG_LABEL[(m.sow_overall_rag || '').toLowerCase()] || '',
    [m.dependency_reason, m.dependency_comment].filter(Boolean).join(': '),
    m.summary || '',
    dateChangesText(changesById[m.id]),
  ]);
}
